<script setup>
import ImgCatClosing from '@manseImages/yongsin-light/img-yongsin-cat-closing-v2.png';
import SurveyCard from '@/components/calendar/drawer/pages/cards/SurveyCard.vue';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';

const analysisStore = useAnalysisStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();
const router = useRouter();
const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const emit = defineEmits(['goToOverView', 'postRate']);

const props = defineProps({
  isMobile: Boolean,
  contentData: {
    type: Object,
    default: null,
  },
  ratingResult: Number,
  ratingStatus: Boolean,
});

// ═══ 왼쪽 페이지 데이터 ═══

const messageLines1 = computed(
  () =>
    props.contentData?.messageLines1 || [
      '나에게 도움이 되는 기운과 에너지를 일상에',
      '하나씩 적용해보세요.',
    ],
);

const messageLines2 = computed(
  () =>
    props.contentData?.messageLines2 || [
      '작은 선택과 습관의 변화가 쌓이면',
      '어느 순간, 흐름이 자연스럽게 바뀌기 시작합니다.',
    ],
);

const highlightMessage = computed(
  () =>
    props.contentData?.highlightMessage ||
    '지금의 나를 이해하는 것에서 좋은 변화가 시작됩니다.',
);

// 용신·희신 카드 데이터
const yongsinInfo = computed(() => props.contentData?.yongsinInfo ?? { name: '', element: '' });
const heesinInfo = computed(() => props.contentData?.heesinInfo ?? { name: '', element: '' });
const energySummaryLines = computed(() => props.contentData?.energySummaryLines ?? []);

// 오행 색상 맵
const ELEMENT_COLOR_MAP = {
  '목': '#5e9f1c',
  '화': '#c23535',
  '토': '#db9823',
  '금': '#949494',
  '수': '#404040',
};

const ELEMENT_LIGHT_BG = {
  '목': '#edf7e4',
  '화': '#fde8e8',
  '토': '#fff6ed',
  '금': '#f0f2f5',
  '수': '#ececec',
};

// 오행 한자 맵
const ELEMENT_HANJA_MAP = {
  '목': '木',
  '화': '火',
  '토': '土',
  '금': '金',
  '수': '水',
};

const yongsinColor = computed(() => ELEMENT_COLOR_MAP[yongsinInfo.value.element] ?? '#c23535');
const heesinColor = computed(() => ELEMENT_COLOR_MAP[heesinInfo.value.element] ?? '#db9823');
const yongsinLightBg = computed(() => ELEMENT_LIGHT_BG[yongsinInfo.value.element] ?? '#fde8e8');
const yongsinHanja = computed(() => ELEMENT_HANJA_MAP[yongsinInfo.value.element] ?? '');
const heesinHanja = computed(() => ELEMENT_HANJA_MAP[heesinInfo.value.element] ?? '');


// ═══ 용신분석 이동 ═══
const goToYongsinAnalysis = () => {
  addContentLogAPI('ay_yongsin_light_btn_go_yongsin');
  if (props.isMobile) {
    const id = mobileSajuLnbStore.guestUserId;
    if (!id) return;
    router.push({ path: '/m/saju-insight/yongsin', query: { id } });
    return;
  }
  analysisStore.clickMainMenu('/overviewYongsin');
};

// ═══ 오른쪽 페이지 이벤트 ═══

const goToOverView = () => {
  addContentLogAPI('ay_yongsin_light_btn_restart');
  emit('goToOverView');
};

const postRate = (score) => {
  emit('postRate', score);
};
</script>

<template>
  <VRow class="full-height">
    <!-- ═══ 왼쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="ysl-card full-height right ysl06-page">
        <div class="ysl06-center-wrap">
          <!-- 1) 상단: 이미지 + 텍스트 카드 -->
          <div class="ysl06-card">
            <div class="ysl06-card__img-wrap">
              <img :src="ImgCatClosing" alt="" class="ysl06-card__img" />
            </div>
            <div class="ysl06-card__text-wrap">
              <div class="ysl06-card__text-block">
                <p v-for="(line, idx) in messageLines1" :key="'m1-' + idx">
                  {{ line }}
                </p>
              </div>
              <div class="ysl06-card__text-block">
                <p v-for="(line, idx) in messageLines2" :key="'m2-' + idx">
                  {{ line }}
                </p>
              </div>
              <p class="ysl06-card__highlight">
                {{ highlightMessage }}
              </p>
            </div>
          </div>

          <!-- 2) 나를 돕는 기운 카드 -->
          <div
            v-if="yongsinInfo.element"
            class="ysl06-energy-card"
            :style="{ background: yongsinLightBg, borderColor: yongsinColor }"
          >
            <!-- 상단 라벨 -->
            <div class="ysl06-energy-card__header">
              <p class="ysl06-energy-card__label">나를 돕는 기운</p>
              <p class="ysl06-energy-card__names">
                <span>중심기운 </span>
                <span :style="{ color: yongsinColor }">{{ yongsinInfo.name }}</span>
                <span> · 보조기운 </span>
                <span :style="{ color: heesinColor }">{{ heesinInfo.name }}</span>
              </p>
            </div>

            <!-- 오행 원형 아이콘 -->
            <div class="ysl06-energy-card__circles">
              <div
                class="ysl06-energy-card__circle"
                :style="{ borderColor: yongsinColor, color: yongsinColor }"
              >
                {{ yongsinHanja }}
              </div>
              <span class="ysl06-energy-card__plus">+</span>
              <div
                class="ysl06-energy-card__circle"
                :style="{ borderColor: heesinColor, color: heesinColor }"
              >
                {{ heesinHanja }}
              </div>
            </div>

            <!-- 따옴표 + 한줄 요약 -->
            <div class="ysl06-energy-card__summary">
              <svg class="ysl06-quote-mark left" width="15" height="12" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.2 0L4.32 7H7.2V14H0V7L4.32 0H7.2ZM18 0L15.12 7H18V14H10.8V7L15.12 0H18Z" :fill="yongsinColor"/>
              </svg>
              <div class="ysl06-energy-card__summary-text">
                <p v-for="(line, idx) in energySummaryLines" :key="idx">{{ line }}</p>
              </div>
              <svg class="ysl06-quote-mark right" width="15" height="12" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.8 14L13.68 7H10.8V0H18V7L13.68 14H10.8ZM0 14L2.88 7H0V0H7.2V7L2.88 14H0Z" :fill="yongsinColor"/>
              </svg>
            </div>
          </div>

          <!-- 3) 용신분석 이동 텍스트 버튼 -->
          <p class="ysl06-deep-link" @click="goToYongsinAnalysis">
            <span>더 깊이 알고싶다면, </span>
            <span class="ysl06-deep-link__accent">전문가용 용신분석</span>
            <span> 도 있어요.</span>
          </p>
        </div>
      </VCard>
    </VCol>

    <!-- ═══ 오른쪽 페이지 (평가하기) ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="ysl-card full-height">
        <div class="ysl06-center-wrap">
          <div class="ysl06-rating">
            <SurveyCard
              style="width: 100%"
              :ratingResult="ratingResult"
              :ratingStatus="ratingStatus"
              @postRate="postRate"
            />
            <p class="ysl06-closing-text">
              우리는 사주를 통해 나를 이해하고,<br />
              용신을 통해 균형을 배우며,<br />
              그 위에서 스스로의 길을 만들어갑니다.
            </p>
            <div class="ysl06-replay">
              <button class="ysl06-replay__btn content_review_button_restart" @click="goToOverView">
                <i class="ysl06-replay__icon" />
                다시보기
              </button>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
// ── 공통 (content1~5와 동일 구조) ──
.ysl-card {
  background: #fff;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.right {
    border-right: 1px solid #ebebeb;
  }
}

// ═══════════════════════════════
// content6 전용
// ═══════════════════════════════
.ysl06-page {
  justify-content: center;
}

.ysl06-center-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 30px;
  flex: 1;
  padding: 20px;
}

// ── 상단 이미지 + 텍스트 카드 ──
.ysl06-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  background: #f7fcff;
  border-radius: 12px;
  padding: 40px 32px;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    padding: 24px 16px;
  }
}

.ysl06-card__img-wrap {
  flex-shrink: 0;
  width: 140px;
  height: 140px;

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
  }
}

.ysl06-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.ysl06-card__text-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.ysl06-card__text-block {
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  color: #333;

  p {
    margin: 0;
  }
}

.ysl06-card__highlight {
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  color: #5d81bf;
  margin: 0;
}

// ── 나를 돕는 기운 카드 ──
.ysl06-energy-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 552px;
  max-width: 100%;
  padding: 30px 26px;
  border-radius: 20px;
  border: 1px solid;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.ysl06-energy-card__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.ysl06-energy-card__label {
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  color: #333;
  margin: 0;
}

.ysl06-energy-card__names {
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: #333;
  margin: 0;
  text-align: center;
}

.ysl06-energy-card__circles {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.ysl06-energy-card__circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1px solid;
  background: #fff;
  font-family: Pretendard;
  font-size: 22px;
  font-weight: 600;
  line-height: 24px;
}

.ysl06-energy-card__plus {
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  color: #757575;
}

.ysl06-energy-card__summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ysl06-energy-card__summary-text {
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #333;
  text-align: center;

  p {
    margin: 0;
  }
}

.ysl06-quote-mark {
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 4px;
}

// ── 용신분석 이동 텍스트 버튼 ──
.ysl06-deep-link {
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: #575757;
  text-align: center;
  margin: 0;
  cursor: pointer;

  &__accent {
    font-weight: 700;
    color: #4e80d6;
    text-decoration: underline;
    cursor: pointer;
  }
}

// ═══════════════════════════════
// content6 오른쪽 (평가하기)
// ═══════════════════════════════
.ysl06-rating {
  height: 100%;
  display: flex;
  padding: 40px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 36px;
  flex: 1 0 0;
  background: #fff;
}

.ysl06-replay {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: 84px;
}

.ysl06-replay__btn {
  display: flex;
  width: 160px;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: 1px solid #ebebeb;
  background: #fff;
  color: #333;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  cursor: pointer;
}

.ysl06-replay__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  background: url('@manseImages/return-icon.svg') center center no-repeat;
  background-size: contain;
}

// ── 클로징 문구 ──
.ysl06-closing-text {
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
  text-align: center;
  margin: 0;
}
</style>
